// =============================================================================
// ApprovalsScreen — /approvals  (T-398)
// The approvals register finally gets a front door. Threshold triggers
// (quote ≥$25k on send, bill ≥$10k past capture — mig 233) create rows here
// and nudge every director; this screen is where they're seen and decided.
// WARN-NOT-BLOCK: the quote/bill was never held up — this is the governance
// record ("two directors saw this money move"), which is exactly what an
// auditor asks for. Directors decide; managers can view.
// =============================================================================

import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ShieldCheck, CheckCircle2, XCircle, AlertTriangle, Clock, ChevronRight, X,
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useRequireAuth, hasRole } from "@/lib/auth";
import { Card, Pill, Button, Spinner, EmptyState, KPI } from "@/components/ui";
import { fmtMoney, fmtDate } from "@/lib/dates";

interface ApprovalRow {
  approval_id:    string;
  approval_number: string;
  subject_table:  string;
  subject_id:     string;
  subject_description: string;
  approval_type:  string;
  amount:         number | null;
  required_approver_count: number;
  status:         string;
  requested_at:   string;
  decided_at:     string | null;
  decisions:      { decision_id: string; approver_user_id: string; decision: string; notes: string | null; decided_at: string }[];
}

const TYPE_LABEL: Record<string, string> = {
  quote_over_25k: "Quote ≥ $25k",
  ap_over_10k:    "Bill ≥ $10k",
};

export default function ApprovalsScreen() {
  const user = useRequireAuth();
  const queryClient = useQueryClient();
  const canView   = hasRole(user, "manager");
  const canDecide = hasRole(user, "director");

  const [tab, setTab] = useState<"pending" | "history">("pending");
  const [err, setErr] = useState<string | null>(null);

  const { data: approvals = [], isLoading } = useQuery({
    queryKey: ["approvals_register", tab],
    enabled: !!user && canView,
    queryFn: async () => {
      let q = supabase
        .from("approvals")
        .select("*, decisions:approval_decisions(decision_id, approver_user_id, decision, notes, decided_at)")
        .order("requested_at", { ascending: false });
      q = tab === "pending" ? q.eq("status", "pending") : q.neq("status", "pending").limit(100);
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as unknown as ApprovalRow[];
    },
  });

  // Resolve decider names (users.user_id → employee / login email)
  const deciderIds = useMemo(
    () => [...new Set(approvals.flatMap(a => a.decisions.map(d => d.approver_user_id)))],
    [approvals],
  );
  const { data: deciders = [] } = useQuery({
    queryKey: ["approvals_deciders", deciderIds.join(",")],
    enabled: deciderIds.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("users")
        .select("user_id, login_email, employees:employee_id(first_name, last_name)")
        .in("user_id", deciderIds);
      if (error) throw error;
      return (data ?? []) as any[];
    },
  });
  const nameOf = (userId: string) => {
    const u = deciders.find((d: any) => d.user_id === userId);
    if (!u) return "director";
    const e = u.employees;
    return e ? `${e.first_name ?? ""} ${e.last_name ?? ""}`.trim() : (u.login_email ?? "director");
  };

  const decideMut = useMutation({
    mutationFn: async ({ id, decision }: { id: string; decision: "approve" | "reject" }) => {
      const { data, error } = await supabase.schema("app").rpc("decide_approval", {
        p_approval_id: id, p_decision: decision, p_notes: null,
      });
      if (error) throw error;
      return data as string;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["approvals_register"] });
      setErr(null);
    },
    onError: (e: any) => setErr(e?.message ?? String(e)),
  });

  const pendingValue = useMemo(
    () => tab === "pending" ? approvals.reduce((s, a) => s + Number(a.amount ?? 0), 0) : 0,
    [approvals, tab],
  );

  if (!user) return null;
  if (!canView) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <Card><EmptyState icon={ShieldCheck} title="Managers only" description="The approvals register is restricted to managers and directors." /></Card>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-[900px] mx-auto pb-20">
      <div className="mb-5">
        <h1 className="text-[22px] md:text-2xl font-semibold tracking-tight flex items-center gap-2">
          <ShieldCheck size={20} className="text-accent" /> Approvals
        </h1>
        <p className="text-text-muted text-[13px] mt-1">
          Big-money sign-offs on the record — quotes ≥ $25k and bills ≥ $10k need two directors.
          Nothing is blocked waiting; this is the governance trail.
        </p>
      </div>

      {err && (
        <Card className="mb-4 border-danger/30">
          <div className="p-3 flex items-center gap-2 text-sm text-danger">
            <AlertTriangle size={15} /> {err}
            <button className="ml-auto" onClick={() => setErr(null)}><X size={14} /></button>
          </div>
        </Card>
      )}

      {tab === "pending" && (
        <div className="grid grid-cols-2 gap-3 mb-4">
          <KPI label="Awaiting sign-off" value={approvals.length} icon={Clock} accent={approvals.length > 0} />
          <KPI label="Value on the table" value={fmtMoney(pendingValue)} />
        </div>
      )}

      <div className="flex items-center gap-1.5 mb-3">
        {(["pending", "history"] as const).map(k => (
          <button key={k} onClick={() => setTab(k)}
            className={`px-3 py-1.5 rounded-full text-[12px] border transition-colors ${
              tab === k ? "bg-accent/15 border-accent/40 text-accent font-medium" : "border-line text-text-muted hover:text-text"
            }`}>
            {k === "pending" ? "Needs sign-off" : "History"}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="py-10 flex justify-center"><Spinner /></div>
      ) : approvals.length === 0 ? (
        <Card><EmptyState icon={CheckCircle2}
          title={tab === "pending" ? "Nothing waiting" : "No decided approvals yet"}
          description={tab === "pending" ? "Every threshold item has been signed off. New ones appear here (and ping the directors) automatically." : "Approve or reject something and it lands here."} /></Card>
      ) : (
        <div className="space-y-2">
          {approvals.map(a => {
            const approvedBy = a.decisions.filter(d => d.decision === "approve");
            const rejectedBy = a.decisions.filter(d => d.decision === "reject");
            const iDecided = a.decisions.some(d => d.approver_user_id === user.user_id);
            const subjectLink =
              a.subject_table === "quotes" ? `/quotes/${a.subject_id}`
              : a.subject_table === "ap_invoices" ? "/ap/xero"
              : null;
            return (
              <Card key={a.approval_id} className={`border-l-2 ${
                a.status === "pending" ? "border-l-warn" : a.status === "approved" ? "border-l-ok" : "border-l-danger"
              }`}>
                <div className="p-4">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-[11px] text-text-muted">{a.approval_number}</span>
                        <Pill label={TYPE_LABEL[a.approval_type] ?? a.approval_type} small />
                        <Pill label={a.status} tone={a.status === "approved" ? "won" : a.status === "rejected" ? "lost" : "warning"} small />
                      </div>
                      <div className="text-[14px] font-medium text-text mt-1">{a.subject_description}</div>
                      <div className="text-[11.5px] text-text-muted mt-0.5">
                        requested {fmtDate(a.requested_at)}
                        {a.decided_at && <> · decided {fmtDate(a.decided_at)}</>}
                        {subjectLink && (
                          <Link to={subjectLink} className="text-accent hover:underline ml-2 inline-flex items-center gap-0.5">
                            open {a.subject_table === "quotes" ? "quote" : "bill"} <ChevronRight size={12} />
                          </Link>
                        )}
                      </div>
                      {/* Who's signed */}
                      <div className="mt-1.5 flex items-center gap-2 flex-wrap text-[12px]">
                        {approvedBy.map(d => (
                          <span key={d.decision_id} className="inline-flex items-center gap-1 text-ok">
                            <CheckCircle2 size={13} /> {nameOf(d.approver_user_id)}
                          </span>
                        ))}
                        {rejectedBy.map(d => (
                          <span key={d.decision_id} className="inline-flex items-center gap-1 text-danger">
                            <XCircle size={13} /> {nameOf(d.approver_user_id)}
                          </span>
                        ))}
                        {a.status === "pending" && (
                          <span className="text-text-muted">
                            {approvedBy.length}/{a.required_approver_count} directors signed
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      {a.amount != null && <div className="font-mono text-[16px] text-text">{fmtMoney(a.amount)}</div>}
                      {a.status === "pending" && canDecide && (
                        <div className="flex items-center gap-2 mt-2">
                          <Button size="sm" variant="primary" icon={CheckCircle2}
                            disabled={decideMut.isPending || iDecided}
                            onClick={() => decideMut.mutate({ id: a.approval_id, decision: "approve" })}>
                            {iDecided ? "You've signed" : "Approve"}
                          </Button>
                          {!iDecided && (
                            <Button size="sm" variant="danger" icon={XCircle}
                              disabled={decideMut.isPending}
                              onClick={() => {
                                if (confirm("Reject this on the record? The quote/bill itself isn't reversed — this flags it for follow-up.")) {
                                  decideMut.mutate({ id: a.approval_id, decision: "reject" });
                                }
                              }}>
                              Reject
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <div className="mt-3 text-[11px] text-text-dim italic">
        Approvals are created automatically when a ≥$25k quote is sent or a ≥$10k bill is coded — every director gets a bell notification.
      </div>
    </div>
  );
}
